/**
 * Photo Configuration page (per-dealer). Each dealer group arranges its own
 * capture and publish sequence and manages its plates here:
 *
 *   - Capture Order: this group's capture sequence per vehicle-type bucket.
 *     Drag-to-reorder. Requires a concrete active group.
 *   - Publish Order: this group's gallery sequence per bucket. Drag-to-reorder.
 *     Requires a concrete active group.
 *   - Dealer Plates: per-group dealer plate / logo configuration.
 *
 * The global, operator-only concerns (the Reference Library, the image-type
 * editor, Test Classification) live on the separate Photo AI (INVI HQ) page.
 * This page still loads the global library, but only so the "Add Image Type
 * from Library" modal can offer the shared vocabulary — dealers pick from it,
 * they don't create types here.
 *
 * Vehicle-type and tab state mirror into URL search params so refresh keeps
 * the operator in place. Shot lists are scoped to the active dealer group;
 * switching groups reloads that group's order.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"

import Layout from "../../components/Layout"
import DealerPlatesView from "../../components/photoConfiguration/DealerPlatesView"
import DealerGroupRequiredNotice from "../../components/DealerGroupRequiredNotice"
import { ReferenceStatusPill } from "../../components/photoConfiguration/photoConfigShared"
import {
  containerStyle,
  emptyStateStyle,
  emptyStateTextStyle,
  errorBannerStyle,
  fieldColumnStyle,
  fieldLabelStyle,
  libraryToolbarLabelStyle,
  libraryToolbarStyle,
  listColumnLabelStyle,
  listContainerStyle,
  listActionsCellStyle,
  listOrderCellStyle,
  listReferenceCellStyle,
  listShotCellStyle,
  listZoomCellStyle,
  loadingTextStyle,
  dragHandleStyle,
  modalActionsRightStyle,
  modalActionsRowStyle,
  modalOverlayStyle,
  modalStyle,
  modalTitleStyle,
  orderListHeaderStyle,
  orderListRowStyle,
  pickerListStyle,
  pickerRowStyle,
  primaryButtonStyle,
  secondaryButtonStyle,
  selectStyle,
  selectorRowStyle,
  shotNameStyle,
  shotSlugStyle,
  smallButtonStyle,
  subtitleStyle,
  tabButtonStyle,
  tabsRowStyle,
  titleStyle,
  zoomSelectStyle,
} from "../../components/photoConfiguration/photoConfigShared"
import { useDealerGroup } from "../../context/DealerGroupContext"
import {
  ALL_BODY_STYLES,
  AddImageTypeToBucketRequest,
  ImageTypeLibrary,
  PhotoConfigurationReorderField,
  PhotoConfigurationShot,
  PhotoConfigurationShotList,
  addImageTypeToBucket,
  getShotList,
  listImageTypes,
  removeImageTypeFromBucket,
  reorderShots,
  seedCanonicalShots,
  updateBucketShot,
} from "../../api/photoConfiguration"

type TabId = "capture_order" | "publish_order" | "dealer_plates"

interface VehicleTypeOption {
  value: string
  label: string
  hasCanonical: boolean
}

const VEHICLE_TYPE_OPTIONS: VehicleTypeOption[] = [
  { value: "truck",          label: "Truck",            hasCanonical: true },
  { value: "suv",            label: "SUV",              hasCanonical: true },
  { value: "sedan_coupe",    label: "Sedan / Coupe",    hasCanonical: true },
  { value: "rv",             label: "RV",               hasCanonical: true },
  { value: "camper_trailer", label: "Camper / Trailer", hasCanonical: true },
]

const DEFAULT_VEHICLE_TYPE = "truck"

const TABS: { id: TabId; label: string }[] = [
  { id: "capture_order",  label: "Capture Order" },
  { id: "publish_order",  label: "Publish Order" },
  { id: "dealer_plates",  label: "Dealer Plates" },
]

const DEFAULT_TAB: TabId = "capture_order"
const VALID_TAB_IDS: ReadonlySet<TabId> = new Set<TabId>([
  "capture_order", "publish_order", "dealer_plates",
])


export default function PhotoConfigurationPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  // Capture/Publish order and Dealer Plates are all per-group; the active
  // group scopes every shot-list call and the plate configuration.
  const { dealerGroupId, activeGroupName } = useDealerGroup()
  const vehicleType = searchParams.get("vehicle_type") ?? DEFAULT_VEHICLE_TYPE
  const tabFromUrl = searchParams.get("tab") as TabId | null
  const activeTab: TabId = tabFromUrl && VALID_TAB_IDS.has(tabFromUrl) ? tabFromUrl : DEFAULT_TAB

  const setVehicleType = (next: string) => {
    const updated = new URLSearchParams(searchParams)
    if (next === DEFAULT_VEHICLE_TYPE) updated.delete("vehicle_type")
    else updated.set("vehicle_type", next)
    setSearchParams(updated, { replace: true })
  }

  const setActiveTab = (next: TabId) => {
    const updated = new URLSearchParams(searchParams)
    if (next === DEFAULT_TAB) updated.delete("tab")
    else updated.set("tab", next)
    setSearchParams(updated, { replace: true })
  }

  // The global image-type library — loaded only to feed the "Add Image Type
  // from Library" picker. Dealers can add an existing type to a bucket but
  // cannot create types here (that's the operator-only Photo AI page).
  const [library, setLibrary] = useState<ImageTypeLibrary | null>(null)

  const [shotList, setShotList] = useState<PhotoConfigurationShotList | null>(null)
  const [bucketLoading, setBucketLoading] = useState<boolean>(true)
  const [bucketError, setBucketError] = useState<string | null>(null)

  const [addingToBucket, setAddingToBucket] = useState<boolean>(false)

  const loadLibrary = useCallback(async () => {
    try {
      const data = await listImageTypes()
      setLibrary(data)
    } catch {
      // The library only powers the "Add Image Type" picker; a failure
      // here shouldn't block the per-dealer ordering UI. The picker
      // surfaces its own empty state when the library is unavailable.
      setLibrary(null)
    }
  }, [])

  const loadShotList = useCallback(async (type: string) => {
    if (dealerGroupId === null) return
    setBucketLoading(true)
    setBucketError(null)
    try {
      const data = await getShotList(type, dealerGroupId)
      setShotList(data)
    } catch (err) {
      setBucketError(err instanceof Error ? err.message : "Failed to load bucket")
      setShotList(null)
    } finally {
      setBucketLoading(false)
    }
  }, [dealerGroupId])

  useEffect(() => {
    void loadLibrary()
  }, [loadLibrary])

  // Reload this group's order whenever the active group or the selected
  // vehicle-type bucket changes — switching groups must show that group's
  // own sequence.
  useEffect(() => {
    if (dealerGroupId === null) {
      setShotList(null)
      setBucketLoading(false)
      return
    }
    let cancelled = false
    setBucketLoading(true)
    setBucketError(null)
    ;(async () => {
      try {
        const data = await getShotList(vehicleType, dealerGroupId)
        if (!cancelled) setShotList(data)
      } catch (err) {
        if (!cancelled) {
          setBucketError(err instanceof Error ? err.message : "Failed to load bucket")
          setShotList(null)
        }
      } finally {
        if (!cancelled) setBucketLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [vehicleType, dealerGroupId])

  const handleSeedCanonical = async () => {
    if (dealerGroupId === null) return
    try {
      await seedCanonicalShots(vehicleType, dealerGroupId)
      await Promise.all([loadLibrary(), loadShotList(vehicleType)])
    } catch (err) {
      setBucketError(err instanceof Error ? err.message : "Failed to seed canonical shots")
    }
  }

  const handleReorder = async (
    field: PhotoConfigurationReorderField,
    orderedIds: number[],
  ) => {
    if (dealerGroupId === null) return
    const entries = orderedIds.map((shot_id, index) => ({ shot_id, position: index + 1 }))
    try {
      const updated = await reorderShots(vehicleType, dealerGroupId, { field, entries })
      setShotList(updated)
    } catch (err) {
      setBucketError(err instanceof Error ? err.message : "Failed to reorder")
      await loadShotList(vehicleType)
    }
  }

  const handleAddImageTypeToBucket = async (request: AddImageTypeToBucketRequest) => {
    if (dealerGroupId === null) return
    setAddingToBucket(false)
    try {
      await addImageTypeToBucket(vehicleType, dealerGroupId, request)
      await loadShotList(vehicleType)
    } catch (err) {
      setBucketError(err instanceof Error ? err.message : "Failed to add image type to bucket")
    }
  }

  const handleRemoveFromBucket = async (shotId: number) => {
    if (dealerGroupId === null) return
    if (!window.confirm(
      "Remove this image type from the bucket? It stays in the Reference Library; only this bucket's pointer is dropped.",
    )) return
    try {
      await removeImageTypeFromBucket(vehicleType, shotId, dealerGroupId)
      await loadShotList(vehicleType)
    } catch (err) {
      setBucketError(err instanceof Error ? err.message : "Failed to remove image type")
    }
  }

  const handleUpdateDefaultZoom = async (shotId: number, defaultZoom: number | null) => {
    if (dealerGroupId === null) return
    try {
      await updateBucketShot(vehicleType, shotId, dealerGroupId, { default_zoom: defaultZoom })
      await loadShotList(vehicleType)
    } catch (err) {
      setBucketError(err instanceof Error ? err.message : "Failed to update start zoom")
    }
  }

  const selectedOption = VEHICLE_TYPE_OPTIONS.find((o) => o.value === vehicleType)
  const isOrderTab = activeTab === "capture_order" || activeTab === "publish_order"

  return (
    <Layout>
      <div style={containerStyle}>
        <h1 style={titleStyle}>Photo Configuration</h1>
        <p style={subtitleStyle}>
          Arrange this dealer's capture and publish sequence and manage its plates.
        </p>

        <div style={tabsRowStyle}>
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={tabButtonStyle(activeTab === tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {isOrderTab && dealerGroupId !== null && (
          <div style={selectorRowStyle}>
            <div style={fieldColumnStyle}>
              <span style={fieldLabelStyle}>Vehicle Type</span>
              <select
                value={vehicleType}
                onChange={(e) => setVehicleType(e.target.value)}
                style={selectStyle}
                aria-label="Vehicle Type"
              >
                {VEHICLE_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {isOrderTab && dealerGroupId !== null && bucketError && (
          <div style={errorBannerStyle}>{bucketError}</div>
        )}

        {isOrderTab && (
          dealerGroupId === null ? (
            <DealerGroupRequiredNotice surface="capture and publish order" />
          ) : bucketLoading ? (
            <div style={loadingTextStyle}>Loading…</div>
          ) : shotList ? (
            <BucketOrderView
              shotList={shotList}
              field={activeTab}
              hasCanonical={selectedOption?.hasCanonical ?? false}
              onSeedCanonical={handleSeedCanonical}
              onReorder={(orderedIds) => handleReorder(activeTab, orderedIds)}
              onAddImageTypeToBucket={() => setAddingToBucket(true)}
              onRemoveFromBucket={handleRemoveFromBucket}
              onUpdateDefaultZoom={handleUpdateDefaultZoom}
            />
          ) : null
        )}

        {activeTab === "dealer_plates" && (
          dealerGroupId === null ? (
            <DealerGroupRequiredNotice surface="dealer plates" />
          ) : (
            // `key` forces a fresh mount when the operator switches groups —
            // otherwise the previous dealer's logo and plates flash briefly
            // while the new ones are fetched.
            <DealerPlatesView
              key={dealerGroupId}
              dealerGroupId={dealerGroupId}
              dealerGroupName={activeGroupName}
            />
          )
        )}

        {addingToBucket && library && shotList && (
          <AddImageTypeToBucketModal
            library={library}
            shotList={shotList}
            onClose={() => setAddingToBucket(false)}
            onAdd={handleAddImageTypeToBucket}
          />
        )}
      </div>
    </Layout>
  )
}


// ---------------------------------------------------------------------------
// Bucket order view — Capture Order and Publish Order tabs
// ---------------------------------------------------------------------------

// The capture app's zoom grid (0.5x–5x in quarter steps). 1.0x is not
// an explicit option — "default" IS 1.0x, stored as null, so the list
// can't hold two spellings of the same meaning.
const START_ZOOM_OPTIONS = Array.from({ length: 19 }, (_, i) => 0.5 + i * 0.25)
  .filter((zoom) => zoom !== 1.0)

function formatStartZoom(zoom: number): string {
  const text = zoom.toFixed(2).replace(/0$/, "")
  return `${text}×`
}

interface BucketOrderViewProps {
  shotList: PhotoConfigurationShotList
  field: PhotoConfigurationReorderField
  hasCanonical: boolean
  onSeedCanonical: () => void
  onReorder: (orderedShotIds: number[]) => void
  onAddImageTypeToBucket: () => void
  onRemoveFromBucket: (shotId: number) => void
  onUpdateDefaultZoom: (shotId: number, defaultZoom: number | null) => void
}

function BucketOrderView({
  shotList,
  field,
  hasCanonical,
  onSeedCanonical,
  onReorder,
  onAddImageTypeToBucket,
  onRemoveFromBucket,
  onUpdateDefaultZoom,
}: BucketOrderViewProps) {
  const sortedShots = useMemo(() => {
    return [...shotList.shots].sort((a, b) =>
      field === "capture_order"
        ? a.capture_order - b.capture_order
        : a.publish_order - b.publish_order,
    )
  }, [shotList.shots, field])

  const [draftOrder, setDraftOrder] = useState<PhotoConfigurationShot[]>(sortedShots)
  const [draggingId, setDraggingId] = useState<number | null>(null)

  useEffect(() => {
    setDraftOrder(sortedShots)
  }, [sortedShots])

  if (shotList.shots.length === 0) {
    return (
      <div style={emptyStateStyle}>
        <p style={emptyStateTextStyle}>
          No image types in the {shotList.display_name} bucket yet.
        </p>
        {hasCanonical && (
          <button onClick={onSeedCanonical} style={primaryButtonStyle}>
            Seed Canonical {shotList.display_name} Shots
          </button>
        )}
        <button onClick={onAddImageTypeToBucket} style={secondaryButtonStyle}>
          + Add Image Type from Library
        </button>
      </div>
    )
  }

  const handleDragStart = (shotId: number) => () => setDraggingId(shotId)
  const handleDragOver = (overShotId: number) => (e: React.DragEvent) => {
    e.preventDefault()
    if (draggingId === null || draggingId === overShotId) return
    setDraftOrder((current) => {
      const next = [...current]
      const fromIndex = next.findIndex((s) => s.id === draggingId)
      const toIndex = next.findIndex((s) => s.id === overShotId)
      if (fromIndex === -1 || toIndex === -1) return current
      const [moved] = next.splice(fromIndex, 1)
      next.splice(toIndex, 0, moved)
      return next
    })
  }
  const handleDrop = () => {
    if (draggingId === null) return
    const newIds = draftOrder.map((s) => s.id)
    const originalIds = sortedShots.map((s) => s.id)
    const changed = newIds.some((id, i) => id !== originalIds[i])
    setDraggingId(null)
    if (changed) onReorder(newIds)
  }

  // Start Zoom is a capture-time knob — the Capture Order tab widens
  // to a fifth column for it; Publish Order keeps the shared four.
  const showZoomColumn = field === "capture_order"
  const gridColumns = showZoomColumn
    ? "120px 1fr 200px 130px 100px"
    : "120px 1fr 200px 100px"

  return (
    <div>
      <div style={libraryToolbarStyle}>
        <span style={libraryToolbarLabelStyle}>
          {sortedShots.length} image type{sortedShots.length === 1 ? "" : "s"} in this bucket
        </span>
        <button onClick={onAddImageTypeToBucket} style={secondaryButtonStyle}>
          + Add Image Type from Library
        </button>
      </div>
      <div style={listContainerStyle}>
        <div style={{ ...orderListHeaderStyle, gridTemplateColumns: gridColumns }}>
          <span style={listColumnLabelStyle}>Order</span>
          <span style={listColumnLabelStyle}>Capture Name</span>
          <span style={listColumnLabelStyle}>References</span>
          {showZoomColumn && <span style={listColumnLabelStyle}>Start Zoom</span>}
          <span style={listColumnLabelStyle}></span>
        </div>
        {draftOrder.map((shot, index) => (
          <div
            key={shot.id}
            draggable
            onDragStart={handleDragStart(shot.id)}
            onDragOver={handleDragOver(shot.id)}
            onDrop={handleDrop}
            onDragEnd={() => setDraggingId(null)}
            style={{ ...orderListRowStyle(draggingId === shot.id), gridTemplateColumns: gridColumns }}
          >
            <span style={listOrderCellStyle}>
              <span aria-label="Drag handle" style={dragHandleStyle}>☰</span>
              <span>{index + 1}.</span>
            </span>
            <span style={listShotCellStyle}>
              <span style={shotNameStyle}>{shot.display_name}</span>
            </span>
            <span style={listReferenceCellStyle}>
              <ReferenceStatusPill count={shot.body_styles_covered} total={ALL_BODY_STYLES.length} />
            </span>
            {showZoomColumn && (
              <span style={listZoomCellStyle}>
                <select
                  value={
                    shot.default_zoom === null || shot.default_zoom === 1
                      ? ""
                      : String(shot.default_zoom)
                  }
                  onChange={(e) => onUpdateDefaultZoom(
                    shot.id,
                    e.target.value === "" ? null : Number(e.target.value),
                  )}
                  onClick={(e) => e.stopPropagation()}
                  style={zoomSelectStyle}
                  aria-label={`Start zoom for ${shot.display_name}`}
                >
                  <option value="">1.0× (default)</option>
                  {START_ZOOM_OPTIONS.map((zoom) => (
                    <option key={zoom} value={zoom}>{formatStartZoom(zoom)}</option>
                  ))}
                </select>
              </span>
            )}
            <span style={listActionsCellStyle}>
              <button
                onClick={(e) => { e.stopPropagation(); onRemoveFromBucket(shot.id) }}
                style={smallButtonStyle}
              >
                Remove
              </button>
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}


// ---------------------------------------------------------------------------
// Add Image Type to Bucket Modal — pick from the library
// ---------------------------------------------------------------------------

function AddImageTypeToBucketModal({
  library,
  shotList,
  onClose,
  onAdd,
}: {
  library: ImageTypeLibrary
  shotList: PhotoConfigurationShotList
  onClose: () => void
  onAdd: (request: AddImageTypeToBucketRequest) => void
}) {
  const alreadyInBucket = useMemo(
    () => new Set(shotList.shots.map((s) => s.image_type_id)),
    [shotList.shots],
  )
  const available = library.image_types.filter((it) => !alreadyInBucket.has(it.id))

  return (
    <div style={modalOverlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <h2 style={modalTitleStyle}>Add Image Type to {shotList.display_name}</h2>
        {available.length === 0 ? (
          <p style={emptyStateTextStyle}>
            Every image type in the library is already in this bucket. Switch to the
            Reference Library tab to create a new image type first.
          </p>
        ) : (
          <div style={pickerListStyle}>
            {available.map((imageType) => (
              <button
                key={imageType.id}
                onClick={() => onAdd({ image_type_id: imageType.id })}
                style={pickerRowStyle}
              >
                <span style={shotNameStyle}>{imageType.display_name}</span>
                <span style={shotSlugStyle}>
                  {imageType.slug} · {imageType.references.length} reference
                  {imageType.references.length === 1 ? "" : "s"}
                </span>
              </button>
            ))}
          </div>
        )}
        <div style={modalActionsRowStyle}>
          <div></div>
          <div style={modalActionsRightStyle}>
            <button onClick={onClose} style={secondaryButtonStyle}>Close</button>
          </div>
        </div>
      </div>
    </div>
  )
}
